import { Component, Optional, inject } from '@angular/core';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { DelegateType } from '../../../shared/models/utility.model';
import { MobileWebViewService } from '../../../core/services/mobile-web-view.service';
import { MobileDelegateService } from '../../../core/services/mobile-delegate.service';
import { Countdown, OtpResentMode } from '../../model/kamonal-model';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { StringHelper } from '../../../core/helpers/strings.helper';
import { ResendOtpModalConfig } from '../kamona-otp-widget/model/kamona-otp-widget.model';
import { AppConfig } from '@mpt-fo/common/shared/config/app-config';

@Component({
    selector: 'ng-mf-kamona-resent-otp-modal',
    templateUrl: './kamona-resent-otp-modal.component.html',
    styleUrls: ['./kamona-resent-otp-modal.component.scss'],
})
export class KamonaResentOtpModalComponent {
    currentMode: OtpResentMode;
    readonly OtpResentMode = OtpResentMode;
    readonly DEFAULT_COUNTDOWN_TIME = 10;
    readonly DEFAULT_SMS_COUNTDOWN: Countdown = { minute: 10, second: 0 };
    defaultWhatsappCountdown: Countdown = { minute: 2, second: 0 };
    smsCountdown: Countdown;
    whatsappCountdown: Countdown;
    ussdCode: string;
    isSmsCountdown: boolean;
    isWhatsAppCountdown: boolean;
    loading: boolean;
    readonly DEFAULT_OTP_RESEND = true;
    canUseUssdOTPResend: boolean;
    canUseSmsOTPResend: boolean;
    canUseWhatsappOTPResend: boolean;
    canUseNewUssd: boolean;
    maskedPhoneNumber: string;
    ussdStyleClass: any;
    otpResendVersion2 = false;
    showBothCountDown = false;
    readonly nzModalData = inject(NZ_MODAL_DATA, { optional: true }) ?? {};
    constructor(
        @Optional() public modal: NzModalRef,
        private mobileService: MobileWebViewService,
        private delegateService: MobileDelegateService,
        private localStorage: LocalStorageService,
        private appConfig: AppConfig
    ) {}

    initialize() {
        this.modal.componentInstance = { ...this.modal.componentInstance, ...this.nzModalData };
        const data = this.modal.componentInstance.data;
        this.setOTPDefaultCountDownValue();
        this.setOTPParameters(data);
        this.handleCountdown();
    }

    setOTPDefaultCountDownValue() {
      const result = this.convertSecondsToMinutesSeconds(this.appConfig.WHATSPP_OTP_COUNTDOWN_IN_SECONDS || 60);
      this.defaultWhatsappCountdown = { minute: result.minutes, second: result.seconds };
    }

    convertSecondsToMinutesSeconds(totalSeconds) {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return { minutes, seconds };
    }

    handleCountdown() {
        if (this.showBothCountDown) {
          this.startSmsCountdown();
          this.startWhatsappCountdown();
          return;
        }
        this.isSmsCountdown ? this.startSmsCountdown() : this.startWhatsappCountdown();
    }

    setOTPParameters(data: ResendOtpModalConfig) {
        this.ussdCode = data?.ussdCode;
        this.showBothCountDown = data?.showBothCountDown;
        if (this.showBothCountDown) {
          this.showBothCountdown(data)
        } else {
          this.isCountdown(data);
        }
        this.setCountdown(data);
        this.setConfig(data);
        const phoneNumber = data?.phoneNumber ?? this.getSavedPhoneNumber();
        this.setMaskPhoneNumber(phoneNumber);
    }

    getSavedPhoneNumber() {
        const businessAddress = this.localStorage.getBusinessAddress();
        return businessAddress?.businessPhone;
    }

    setConfig(data: ResendOtpModalConfig) {
        this.canUseUssdOTPResend = data?.canUseUssdOTPResend || this.DEFAULT_OTP_RESEND;
        this.canUseSmsOTPResend = data?.canUseSmsOTPResend ?? this.DEFAULT_OTP_RESEND;
        this.canUseWhatsappOTPResend = data?.canUseWhatsappOTPResend;
        this.canUseNewUssd = data?.canUseNewUssd;
        this.currentMode = data?.currentMode;
        this.otpResendVersion2 = data?.canUseResendOptV2;
    }

    setCountdown(data: any) {
        this.whatsappCountdown = data?.whatsappCountdown;
        this.smsCountdown = data?.smsCountdown;
    }

    isCountdown(data: any) {
        this.isSmsCountdown = this.isStartCountdown(data?.smsCountdown) && data?.currentMode === OtpResentMode.SMS_MODE;
        this.isWhatsAppCountdown = this.isStartCountdown(data?.whatsappCountdown) && data?.currentMode === OtpResentMode.WHATSAPP_MODE;
    }

    showBothCountdown(data: any) {
      this.isSmsCountdown = this.isStartCountdown(data?.smsCountdown);
      this.isWhatsAppCountdown = this.isStartCountdown(data?.whatsappCountdown);
  }

    setMaskPhoneNumber(phoneNumber: string) {
        if (!phoneNumber) {
            return;
        }
        const NO_OF_CHARACTERS = 4;
        this.maskedPhoneNumber = StringHelper.maskMobilePhoneNumber(phoneNumber, NO_OF_CHARACTERS);
    }

    setUssdStyleClass() {
        const isCurrentMode = this.currentMode === OtpResentMode.USSD_MODE;
        this.ussdStyleClass = {
            'opacity-60': isCurrentMode,
        };
    }

    isStartCountdown(countdown: { minute: number; second: number }) {
        if (countdown?.minute > 0) {
            return true;
        }
        return countdown?.minute === 0 && countdown?.second > 0;
    }

    startSmsCountdown() {
        const countdown = this.isExpiredCountdown(this.smsCountdown) ? this.DEFAULT_SMS_COUNTDOWN : this.smsCountdown;
        const startMinutes = countdown?.second / 60 + countdown?.minute;
        let time = startMinutes * 60;
        const interval = setInterval(() => {
            const minute = this.getMinutes(time);
            const second = this.getSeconds(time);
            this.smsCountdown = { minute, second };
            time--;
            if (this.isExpiredCountdown(this.smsCountdown)) {
              this.isSmsCountdown = false;
                clearInterval(interval);
            }
        }, 1000);
    }

    isExpiredCountdown(countdown: Countdown) {
        if (!countdown) {
            return true;
        }
        return countdown?.minute === 0 && countdown?.second === 0;
    }

    startWhatsappCountdown() {
        const countdown = this.isExpiredCountdown(this.whatsappCountdown) ? this.defaultWhatsappCountdown : this.whatsappCountdown;
        const startMinutes = countdown?.second / 60 + countdown?.minute;
        let time = startMinutes * 60;
        const interval = setInterval(() => {
            const minute = this.getMinutes(time);
            const second = this.getSeconds(time);
            this.whatsappCountdown = { minute, second };
            time--;
            if (this.isExpiredCountdown(this.whatsappCountdown)) {
                this.isWhatsAppCountdown = false;
                clearInterval(interval);
            }
        }, 1000);
    }

    getSeconds(time: number) {
        return Math.floor(time % 60);
    }

    getMinutes(time: number) {
        return Math.floor(time / 60);
    }

    canSkipCountDown() {
        return this.otpResendVersion2;
    }

    resendOtpViaSMS() {
        const canContinue = !this.otpResendVersion2 && this.isSmsCountdown ? this.isCountdownFinished(this.smsCountdown) : true;
        if (!canContinue) {
            return;
        }
        this.currentMode = OtpResentMode.SMS_MODE;
        this.loading = true;
        this.regenerateOtp(this.smsCountdown);
    }

    resendOtpViaWhatsApp() {
        const canContinue = !this.otpResendVersion2 && this.isWhatsAppCountdown ? this.isCountdownFinished(this.whatsappCountdown) : true;
        if (!canContinue) {
            return;
        }
        this.currentMode = OtpResentMode.WHATSAPP_MODE;
        this.loading = true;
        this.regenerateOtp(this.whatsappCountdown);
    }

    isCountdownFinished(countdown: Countdown) {
        const minutes = countdown?.minute;
        const seconds = countdown?.second;
        return minutes <= 0 && seconds <= 0;
    }

    regenerateOtp(countdown: Countdown) {
        const params = { countdown, currentMode: this.currentMode };
        this.modal.componentInstance.callback(params);
    }

    resendOTPViaUssd() {
        this.currentMode = OtpResentMode.USSD_MODE;
        this.setUssdStyleClass();
        this.canUseNewUssd ? this.handleNewUssdFlow() : this.handleOldUssdFlow();
    }

    handleNewUssdFlow() {
        const params = { currentMode: this.currentMode };
        this.modal.componentInstance.callback(params);
        this.closeModal();
    }

    handleOldUssdFlow() {
        this.mobileService.isMobileWebViewUser() ? this.launchMobileDialer(this.ussdCode) : this.launchWebDialer(this.ussdCode);
    }

    closeModal() {
        this.modal.destroy();
    }

    launchWebDialer(value: any) {
        window.open(`tel:+${value}`);
        this.closeModal();
    }

    launchMobileDialer(value: any) {
        this.delegateService
            .setDelegateData(DelegateType.launchDialer, value)
            .then(() => {
                this.closeModal();
            })
            .catch((error: any) => {
                this.launchWebDialer(value);
            });
    }

    formatTime = (t: number) => {
        return t < 10 ? '0' + t : t;
    };
}
